import { Button } from '@heroui/button';
import { Card, CardBody, CardHeader } from '@heroui/card';
import { Divider } from '@heroui/divider';
import { Input } from '@heroui/input';
import { Modal, ModalBody, ModalContent, ModalHeader } from '@heroui/modal';
import { Spinner } from '@heroui/spinner';
import { addToast } from '@heroui/toast';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { type FC, useState } from 'react';

import {
  getEmailTemplatesControllerListQueryKey,
  getEmailTemplatesControllerVersionsQueryKey,
  useEmailTemplatesControllerList,
  useEmailTemplatesControllerPreview,
  useEmailTemplatesControllerSave,
  useEmailTemplatesControllerVersions,
} from '@/api/generated/email-templates/email-templates';
import type { EmailTemplateDto, RenderedEmailDto } from '@/api/generated/schemas';
import { PageHeader } from '@/components/common/PageHeader';
import { EmailBodyEditor } from '@/components/email';
import { extractBackendErrorMessage } from '@/utils/apiError';
import { formatDateTimeAu } from '@/utils/formatting';

export const Route = createFileRoute('/settings/email-templates')({
  component: EmailTemplates,
});

function EmailTemplates() {
  const { data: templates, isLoading } = useEmailTemplatesControllerList();

  return (
    <div className="py-6">
      <div className="flex flex-col gap-6">
        <PageHeader
          title="Email templates"
          description="The starting copy for emails Relay sends. Changes apply to the next email sent; every save is kept as a version."
        />
        {isLoading && <Spinner />}
        {templates?.map((template) => (
          <TemplateEditor key={`${template.purpose}-${template.version}`} template={template} />
        ))}
      </div>
    </div>
  );
}

const TemplateEditor: FC<{ template: EmailTemplateDto }> = ({ template }) => {
  const queryClient = useQueryClient();
  const [subject, setSubject] = useState(template.subject);
  const [bodyHtml, setBodyHtml] = useState(template.bodyHtml);
  const [preview, setPreview] = useState<RenderedEmailDto | null>(null);
  const saveMutation = useEmailTemplatesControllerSave<unknown>();
  const previewMutation = useEmailTemplatesControllerPreview<unknown>();
  const isDirty = subject !== template.subject || bodyHtml !== template.bodyHtml;

  const save = async () => {
    try {
      const saved = await saveMutation.mutateAsync({ purpose: template.purpose, data: { subject, bodyHtml } });
      addToast({ title: 'Template saved', description: `Now on version ${saved.version}.`, color: 'success' });
      void queryClient.invalidateQueries({ queryKey: getEmailTemplatesControllerListQueryKey() });
      void queryClient.invalidateQueries({ queryKey: getEmailTemplatesControllerVersionsQueryKey(template.purpose) });
    } catch (error) {
      addToast({
        title: 'Template not saved',
        description: extractBackendErrorMessage(error, 'Failed to save the template'),
        color: 'danger',
      });
    }
  };

  const showPreview = async () => {
    try {
      setPreview(await previewMutation.mutateAsync({ purpose: template.purpose, data: { subject, bodyHtml } }));
    } catch (error) {
      addToast({
        title: 'Preview failed',
        description: extractBackendErrorMessage(error, 'Failed to render the preview'),
        color: 'danger',
      });
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-col items-start gap-1 px-6 pt-5">
        <h3 className="text-lg font-semibold">{template.label}</h3>
        <p className="text-sm text-default-500">
          Sent from {template.mailbox}.{' '}
          {template.version === 0
            ? 'Built-in draft: replace it before go-live.'
            : `Version ${template.version}, saved ${formatDateTimeAu(template.updatedAt)} by ${template.updatedBy ?? 'unknown'}.`}
        </p>
      </CardHeader>
      <Divider />
      <CardBody className="px-6 py-5 gap-4">
        <Input label="Subject" value={subject} onValueChange={setSubject} isDisabled={saveMutation.isPending} />
        <EmailBodyEditor
          initialHtml={template.bodyHtml}
          onChange={setBodyHtml}
          variables={template.variables}
          isDisabled={saveMutation.isPending}
        />
        <ul className="text-xs text-default-500 space-y-0.5">
          {template.variables.map((variable) => (
            <li key={variable.name}>
              <code className="text-default-700">{`{{${variable.name}}}`}</code> {variable.description}
              {variable.block ? ' (body only, on its own line)' : ''}
            </li>
          ))}
        </ul>
        <div className="flex gap-2 justify-end">
          <Button variant="flat" onPress={() => void showPreview()} isLoading={previewMutation.isPending}>
            Preview
          </Button>
          <Button color="primary" onPress={() => void save()} isLoading={saveMutation.isPending} isDisabled={!isDirty}>
            Save
          </Button>
        </div>
        <VersionHistory purpose={template.purpose} />
      </CardBody>
      <Modal isOpen={preview !== null} onClose={() => setPreview(null)} size="3xl" scrollBehavior="inside">
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            <span className="text-xs font-normal text-default-500">Preview with sample values</span>
            <span>{preview?.subject}</span>
          </ModalHeader>
          <ModalBody className="pb-6">
            {/* Sandboxed: template HTML is user-authored, so it never runs as part of Relay. */}
            <iframe
              title="Email preview"
              sandbox=""
              srcDoc={preview?.bodyHtml}
              className="w-full min-h-[480px] rounded-medium border border-default-200 bg-white"
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Card>
  );
};

const VersionHistory: FC<{ purpose: EmailTemplateDto['purpose'] }> = ({ purpose }) => {
  const { data: versions } = useEmailTemplatesControllerVersions(purpose);
  if (!versions || versions.length === 0) return null;

  return (
    <details className="text-sm">
      <summary className="cursor-pointer text-default-500">Version history ({versions.length})</summary>
      <ul className="mt-2 space-y-1 text-default-600">
        {versions.map((version) => (
          <li key={version.version}>
            Version {version.version}: {formatDateTimeAu(version.createdAt)} by {version.createdBy ?? 'unknown'}
          </li>
        ))}
      </ul>
    </details>
  );
};
